import {
  calcularTotalGB,
  calcularTotalValor,
  type ComposicaoProposta,
  type LinhaComposicao,
  type PlanoCNPJ,
  type RenovacaoSimulacao,
} from '../constants/planosCNPJ';
import { normalizarMsisdn } from '../../../backend/src/services/propostas/normalizar-msisdn';
import { renovacaoMantemPacote, tmDaRenovacao } from './carteiraClaro/calcularAptidaoCarteira';

export interface LinhaFaturaRenovacao {
  faturaUploadId?: string;
  contaNumero?: string;
  competenciaFatura?: string;
  numero: string;
  gbAtual?: number;
  planoAtual?: string;
  valorAtual?: number;
  sugestaoPlanoId?: string | null;
  sugestaoPlanoNome?: string | null;
  sugestaoValor?: number;
  sugestaoDelta?: number | null;
  sugestaoFaixa?: number | null;
  sugestaoAcrescimo?: number;
  sugestaoTicketAlvoFaixa1?: number | null;
  sugestaoMotivo?: string;
  dataFidelidade?: string | null;
}

const arredondarMoeda = (valor: number) => Math.round(valor * 100) / 100;

const gbDoNome = (nome?: string | null): number | null => {
  const encontrado = nome?.match(/(\d+(?:[.,]\d+)?)\s*GB/i)?.[1];
  if (!encontrado) return null;
  const valor = Number(encontrado.replace(',', '.'));
  return Number.isFinite(valor) ? valor : null;
};

const mesmoValor = (a?: number | null, b?: number | null) =>
  a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.005;

/**
 * UM NÚMERO = UMA LINHA da fatura.
 *
 * `renovacaoEstaSincronizadaComFatura` exige um número distinto por linha da
 * fatura. Fatura com dois números que normalizam igual — ou com linha sem
 * número — deixava o predicado FALSO para sempre: o reconciliador rodava a cada
 * render, emitia composição nova, e a proposta ficava tremendo na tela
 * (Douglas 01/09/2026). Deduplicar aqui é o que torna o predicado alcançável, e
 * é a mesma régua que o save já aplicava ao gravar (`_dedupTel`).
 *
 * As duas funções abaixo TÊM de usar esta lista — se uma deduplicar e a outra
 * não, as contagens divergem e o laço volta por outro caminho.
 */
const dedupLinhasFatura = (linhas: LinhaFaturaRenovacao[]): LinhaFaturaRenovacao[] => {
  const vistos = new Set<string>();
  return linhas.filter((linha) => {
    const msisdn = normalizarMsisdn(linha.numero);
    if (vistos.has(msisdn)) return false;
    vistos.add(msisdn);
    return true;
  });
};

export function resolverPlanoRenovacaoCompativel(
  linhaSalva: LinhaComposicao | undefined,
  linhaFatura: LinhaFaturaRenovacao,
  planos: PlanoCNPJ[],
  pacoteMinimoGb: number | null = null,
  sugerir = true,
): PlanoCNPJ | undefined {
  const disponivel = (plano: PlanoCNPJ | undefined) => Boolean(
    plano && (pacoteMinimoGb === null || Number(plano.gb || 0) >= pacoteMinimoGb),
  );
  const planoExato = linhaSalva?.planoId
    ? planos.find((plano) => plano.id === linhaSalva.planoId)
    : undefined;
  if (disponivel(planoExato)) return planoExato;

  const valorSalvo = linhaSalva
    ? Number(linhaSalva.receitaRenovada ?? linhaSalva.ticketNovo ?? linhaSalva.plano?.valor ?? 0)
    : 0;
  const gbSalvo = linhaSalva
    ? gbDoNome(linhaSalva.planoNovo || linhaSalva.plano?.nome) ?? linhaSalva.plano?.gb ?? null
    : null;
  const planoLegado = planos.find((plano) =>
    gbSalvo != null
    && Number(plano.gb) === gbSalvo
    && mesmoValor(plano.valor, valorSalvo),
  );
  if (disponivel(planoLegado)) return planoLegado;
  const planosMesmoValor = planos.filter((plano) =>
    disponivel(plano) && mesmoValor(plano.valor, valorSalvo),
  );
  if (planosMesmoValor.length === 1) return planosMesmoValor[0];
  if (!sugerir) return undefined;

  const sugestaoExata = linhaFatura.sugestaoPlanoId
    ? planos.find((plano) => plano.id === linhaFatura.sugestaoPlanoId)
    : undefined;
  if (disponivel(sugestaoExata)) return sugestaoExata;

  const gbSugerido = gbDoNome(linhaFatura.sugestaoPlanoNome);
  return planos.find((plano) =>
    disponivel(plano)
    && gbSugerido != null
    && Number(plano.gb) === gbSugerido
    && mesmoValor(plano.valor, linhaFatura.sugestaoValor),
  ) ?? planos.find((plano) => disponivel(plano) && mesmoValor(plano.valor, linhaFatura.sugestaoValor));
}

export function reconciliarRenovacaoComFatura(
  composicao: ComposicaoProposta | null | undefined,
  linhasFaturaTodas: LinhaFaturaRenovacao[],
  planos: PlanoCNPJ[],
  meta: RenovacaoSimulacao,
  tempoContratoMeses?: number | null,
  classificacao?: string | null,
  opcoes: { sugerirPlano?: boolean } = {},
): ComposicaoProposta | null {
  const sugerirPlano = opcoes.sugerirPlano ?? true;
  // Linha EXCLUÍDA pelo consultor não volta: a fatura manda nos dados, a
  // exclusão é decisão de quem monta a proposta (Douglas 29/08).
  const excluidas = new Set((meta.linhasExcluidas || []).map(normalizarMsisdn));
  const linhasDaConta = dedupLinhasFatura(linhasFaturaTodas);
  const linhasFatura = linhasDaConta.filter(
    (linha) => !excluidas.has(normalizarMsisdn(linha.numero)),
  );
  if (linhasFaturaTodas.length === 0) return composicao ?? null;
  // O pacote mínimo segue o TM da trava do pedido: na parcial, o das linhas
  // renovadas pela fidelidade na conta (Douglas 26/09/2026).
  const tmDaRegra = tmDaRenovacao({
    tmCarteira: tempoContratoMeses,
    parcial: linhasFatura.length < linhasDaConta.length,
    numerosRenovados: linhasFatura.map((linha) => linha.numero),
    linhasConta: linhasFaturaTodas,
  }).tm;
  const mantemPacote = renovacaoMantemPacote(tmDaRegra, classificacao);

  const salvas = composicao?.linhas.filter((linha) => linha.tipo === 'RENOVACAO') ?? [];
  const extras = composicao?.linhas.filter((linha) => linha.tipo !== 'RENOVACAO') ?? [];
  const salvaPorNumero = new Map(
    salvas
      .map((linha) => [normalizarMsisdn(linha.numero), linha] as const)
      .filter(([numero]) => numero.length >= 10),
  );

  const renovacoes = linhasFatura.map((linhaFatura, index): LinhaComposicao => {
    const msisdn = normalizarMsisdn(linhaFatura.numero);
    const salva = salvaPorNumero.get(msisdn);
    const pacoteMinimoGb = mantemPacote ? Number(linhaFatura.gbAtual || 0) : null;
    const plano = resolverPlanoRenovacaoCompativel(salva, linhaFatura, planos, pacoteMinimoGb, sugerirPlano);
    const planoSugerido = resolverPlanoRenovacaoCompativel(undefined, linhaFatura, planos, pacoteMinimoGb);
    const receitaRenovada = plano
      ? Number(plano.valor)
      : Number(salva?.receitaRenovada ?? salva?.ticketNovo ?? 0);
    const receitaAdicionada = Number(salva?.receitaAdicionada || 0);
    const sugestaoAplicada = Boolean(
      plano
      && planoSugerido
      && plano.id === planoSugerido.id
      && mesmoValor(plano.valor, linhaFatura.sugestaoValor),
    );

    return {
      ...salva,
      id: salva?.id || `renovacao-fatura-${msisdn || index}`,
      linha_id: salva?.linha_id || crypto.randomUUID(),
      tipo: 'RENOVACAO',
      numero: linhaFatura.numero,
      faturaUploadId: linhaFatura.faturaUploadId,
      contaNumero: linhaFatura.contaNumero,
      competenciaFatura: linhaFatura.competenciaFatura,
      gbAtual: Number(linhaFatura.gbAtual || 0),
      planoAtual: linhaFatura.planoAtual || '',
      valorAtual: Number(linhaFatura.valorAtual || 0),
      planoId: plano?.id || null,
      plano,
      planoNovo: plano?.nome || '',
      receitaRenovada,
      receitaAdicionada,
      ticketNovo: receitaRenovada + receitaAdicionada,
      sugestaoRenovacao: planoSugerido && linhaFatura.sugestaoPlanoId ? {
        origem: 'ANALISE_FATURA',
        planoId: planoSugerido.id,
        planoNome: planoSugerido.nome,
        ticketNovo: Number(linhaFatura.sugestaoValor || planoSugerido.valor),
        deltaPercentual: linhaFatura.sugestaoDelta ?? null,
        faixa: linhaFatura.sugestaoFaixa ?? null,
        acrescimoReceita: Number(linhaFatura.sugestaoAcrescimo || 0),
        ticketAlvoFaixa1: linhaFatura.sugestaoTicketAlvoFaixa1 ?? null,
        motivo: linhaFatura.sugestaoMotivo || 'Sugestão gerada durante a análise da fatura',
        aplicadaAutomaticamente: sugestaoAplicada,
      } : salva?.sugestaoRenovacao,
    };
  });

  const linhas = [...renovacoes, ...extras];
  const incluiFibra = linhas.some((linha) => linha.tipo === 'FIBRA');
  return {
    ...(composicao ?? {
      incluiFibra,
      totalGB: 0,
      totalValor: 0,
      economiaValor: 0,
      economiaPercent: 0,
    }),
    linhas,
    incluiFibra,
    totalGB: calcularTotalGB(linhas, incluiFibra),
    totalValor: arredondarMoeda(calcularTotalValor(linhas)),
    renovacao: {
      ...metaSemListaVelha(meta),
      ...(linhasFaturaTodas.some(linha => linha.faturaUploadId) ? {
        faturaUploadIds: [...new Set(linhasFaturaTodas.map(linha => linha.faturaUploadId).filter((id): id is string => Boolean(id)))],
      } : {}),
    },
  };
}

function metaSemListaVelha(meta: RenovacaoSimulacao): RenovacaoSimulacao {
  const { faturaUploadIds, ...resto } = meta as RenovacaoSimulacao & { faturaUploadIds?: string[] };
  if (Array.isArray(faturaUploadIds) && meta.faturaUploadId && !faturaUploadIds.includes(meta.faturaUploadId)) return resto;
  return meta;
}

// A análise da fatura só entra nas linhas quando o vendedor aperta "Aplicar nas
// linhas" (Douglas 02/10/2026): o plano escolhido antes é descartado e cada
// linha recebe o plano sugerido pela fatura, respeitando o pacote mínimo do TM.
export function aplicarSugestaoFaturaRenovacao(
  composicao: ComposicaoProposta | null | undefined,
  linhasFaturaTodas: LinhaFaturaRenovacao[],
  planos: PlanoCNPJ[],
  meta: RenovacaoSimulacao,
  tempoContratoMeses?: number | null,
  classificacao?: string | null,
): ComposicaoProposta | null {
  const semPlano = composicao ? {
    ...composicao,
    linhas: composicao.linhas.map((linha) => linha.tipo === 'RENOVACAO' ? {
      ...linha,
      planoId: null,
      plano: undefined,
      planoNovo: '',
      receitaRenovada: undefined,
      ticketNovo: undefined,
    } : linha),
  } : composicao;
  return reconciliarRenovacaoComFatura(semPlano, linhasFaturaTodas, planos, meta, tempoContratoMeses, classificacao, { sugerirPlano: true });
}

export function renovacaoEstaSincronizadaComFatura(
  composicao: ComposicaoProposta | null | undefined,
  linhasFaturaTodas: LinhaFaturaRenovacao[],
  linhasExcluidas?: string[] | null,
): boolean {
  // A régua compara contra a fatura SEM as linhas que o consultor excluiu —
  // senão toda exclusão dispara o reconciliador e a linha "volta".
  const excluidas = new Set((linhasExcluidas || []).map(normalizarMsisdn));
  const linhasFatura = dedupLinhasFatura(linhasFaturaTodas).filter(
    (linha) => !excluidas.has(normalizarMsisdn(linha.numero)),
  );
  if (linhasFatura.length === 0) return true;
  const renovacoes = composicao?.linhas.filter((linha) => linha.tipo === 'RENOVACAO') ?? [];
  if (renovacoes.length !== linhasFatura.length) return false;

  const porNumero = new Map(
    renovacoes.map((linha) => [normalizarMsisdn(linha.numero), linha] as const),
  );
  if (porNumero.size !== linhasFatura.length) return false;

  return linhasFatura.every((linhaFatura) => {
    const linha = porNumero.get(normalizarMsisdn(linhaFatura.numero));
    if (!linha) return false;
    const mesmoPlano = String(linha.planoAtual || '').trim() === String(linhaFatura.planoAtual || '').trim();
    const mesmoTicket = Math.abs(Number(linha.valorAtual || 0) - Number(linhaFatura.valorAtual || 0)) < 0.005;
    const mesmoGb = Math.abs(Number(linha.gbAtual || 0) - Number(linhaFatura.gbAtual || 0)) < 0.005;
    const mesmaFatura = !linhaFatura.faturaUploadId || linha.faturaUploadId === linhaFatura.faturaUploadId;
    return mesmoPlano && mesmoTicket && mesmoGb && mesmaFatura;
  });
}

export function assinaturaFaturaRenovacao(
  leadId: string | undefined,
  faturaUploadId: string | null | undefined,
  linhas: LinhaFaturaRenovacao[],
): string {
  const detalhe = linhas.map((linha) => [
    normalizarMsisdn(linha.numero),
    linha.planoAtual || '',
    Number(linha.valorAtual || 0).toFixed(2),
    Number(linha.gbAtual || 0),
    linha.faturaUploadId || '',
    linha.contaNumero || '',
    linha.competenciaFatura || '',
  ].join('|')).join(';');
  return `${leadId || ''}|${faturaUploadId || ''}|${detalhe}`;
}
